import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import ReactPaginate from "react-paginate";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  fetchVideos,
  deleteVideo,
  fetchVideoTags,
} from "../../../store/reducers/videoSectionSlice";

import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";

const buildDropdownOptions = (items = []) =>
  [...new Set(items.map((item) => `${item}`.trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((item) => ({
      label: item,
      value: item,
    }));

const VideoSection = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { videos, loading, total, videoTags } = useSelector(
    (state) => state.eduVideos
  );

  const itemsPerPage = 10;

  const [currentPage, setCurrentPage] = useState(1);
  const [nameFilter, setNameFilter] = useState("");
  const [debouncedName, setDebouncedName] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const tagOptions = buildDropdownOptions(videoTags || []);

  useEffect(() => {
    dispatch(fetchVideoTags());
  }, [dispatch]);

  /* SEARCH DEBOUNCE */

  useEffect(() => {
    const handler = _.debounce((value) => {
      setDebouncedName(value);
      setCurrentPage(1);
    }, 500);

    handler(nameFilter);

    return () => handler.cancel();
  }, [nameFilter]);

  /* FETCH VIDEOS */

  useEffect(() => {
    dispatch(
      fetchVideos({
        search: debouncedName,
        tag: selectedTag?.value || "",
        page: currentPage,
        limit: itemsPerPage,
        key: "all",
      })
    );
  }, [dispatch, debouncedName, currentPage, selectedTag]);

  const pageCount = Math.ceil(total / itemsPerPage);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1);
  };

  const handleNameFilterChange = (e) => {
    setNameFilter(e.target.value);
  };

  const handleTagChange = (selectedOption) => {
    setSelectedTag(selectedOption);
    setCurrentPage(1);
  };

  const handleAddVideo = () => {
    history.push("/admin-manage-edu-videos");
  };

  const handleEditVideo = (item) => {
    history.push(`/admin-manage-edu-videos?id=${item.id}`);
  };

  /* OPEN DELETE MODAL */

  const handleDeleteClick = (id) => {
    setSelectedVideoId(id);
    setShowDeleteModal(true);
  };

  /* CONFIRM DELETE */

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteVideo(selectedVideoId)).unwrap();

      toast.success("Video deleted successfully");

      setShowDeleteModal(false);
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      {/* HEADER */}

      <div className="row my-2 align-items-center">
        <div className="col-12 d-flex flex-column flex-md-row align-items-center justify-content-between">
          <div className="fw-bold mb-2 mb-md-0">Educational Video</div>

          {/* SEARCH */}

          <div
            className="d-flex gap-2 mx-auto align-items-center"
            style={{ maxWidth: 760, width: "100%" }}
          >
            <input
              type="text"
              className="form-control"
              placeholder="Search by Video name"
              value={nameFilter}
              onChange={handleNameFilterChange}
            />

            <div style={{ minWidth: 220, width: "100%" }}>
              <Select
                isClearable
                options={tagOptions}
                value={selectedTag}
                onChange={handleTagChange}
                placeholder="Search by Tag"
              />
            </div>

            {(nameFilter || selectedTag) && (
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setNameFilter("");
                  setSelectedTag(null);
                  setCurrentPage(1);
                }}
              >
                Clear
              </button>
            )}
          </div>

          <button
            className="btn bg_color"
            type="button"
            onClick={handleAddVideo}
          >
            Add Educational Video
          </button>
        </div>
      </div>

      {/* TABLE */}

      <table className="table">
        <thead>
          <tr>
            <th>Sr. No</th>
            <th>Video ID</th>
            <th>Name</th>
            <th>Status</th>
            <th style={{ width: 70 }}>Edit</th>
            <th style={{ width: 80 }}>Remove</th>
          </tr>
        </thead>

        <tbody>
          {loading && (
            <tr>
              <td colSpan="7" className="text-center">
                Loading...
              </td>
            </tr>
          )}

          {!loading && videos.length === 0 && (
            <tr>
              <td colSpan="7" className="text-center">
                Data Not Found
              </td>
            </tr>
          )}

          {videos.map((item, i) => (
            <tr key={item.id}>
              <td>{i + 1 + itemsPerPage * (currentPage - 1)}</td>
              <td>{item.id}</td>
              <td>{item.name}</td>
              <td>
                {item.active ? (
                  <span className="text-success fw-semibold">Active</span>
                ) : (
                  <span className="text-danger fw-semibold">Inactive</span>
                )}
              </td>

              {/* EDIT */}

              <td>
                <i
                  className="nav-icon fas fa-edit btn btn-sm bg_color"
                  onClick={() => handleEditVideo(item)}
                ></i>
              </td>

              {/* DELETE */}

              <td>
                <button
                  className="btn btn-sm bg_color"
                  onClick={() => handleDeleteClick(item.id)}
                >
                  <i className="nav-icon fas fa-trash"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* PAGINATION */}

      <ReactPaginate
        nextLabel="Next"
        previousLabel="Previous"
        onPageChange={handlePageClick}
        pageCount={pageCount}
        forcePage={currentPage - 1}
        pageRangeDisplayed={5}
        pageClassName="page-item"
        pageLinkClassName="page-link"
        previousClassName="page-item"
        previousLinkClassName="page-link"
        nextClassName="page-item"
        nextLinkClassName="page-link"
        containerClassName="pagination"
        activeClassName="active"
      />

      {/* DELETE MODAL */}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>

        <Modal.Body>Are you sure you want to delete this video?</Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>

          <Button variant="danger" onClick={handleConfirmDelete}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VideoSection;
